/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, inject, signal, effect, OnInit, computed } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AutoComplete, AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonsComponent } from '@/app/shared/buttons/buttons.component';
import { DesyAngularModule } from 'desy-angular';
import { SearchService } from '@/app/services/ia-services/search.service';
import { ProjectsService } from '@/app/services/ia-services/projects.service';
import { PillComponent } from '@/app/shared/pill/pill.component';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectButtonModule } from 'primeng/selectbutton';
import { MultiSelectModule } from 'primeng/multiselect';
import { Location } from '@angular/common';
import { ConfirmationService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { getError } from '@/app/utils/form-errors';
import { requiredNonEmptyArray } from '@/app/utils/required-non-empty-array.validator';
import { integerOnlyValidator } from '@/app/utils/integer-validation';
import { groupAdministrationFormatValidator } from '@/app/utils/group-administration-format.validator';
import { ProjectCorrectionPayload, ProjectDetails } from '@/app/interfaces/project';
import { anjanaFieldToReviewFieldMap } from '@/app/services/ia-services/review-fields.mapper';
import { ReviewFieldKey } from '@/app/interfaces/review-fields.types';
import { ObservedFieldAlertComponent } from '@/app/shared/observed-field-alert/observed-field-alert/observed-field-alert.component';
import { ReviewCommentsBlockComponent } from '@/app/shared/review-comments-block/review-comments-block.component';
import { environment } from '@/environments/environment';

interface EmployeeRowValue {
  totalPeople: number | null;
  groupAdministration: string | null;
  levelPosition: number | null;
}

type EmployeeObservedFieldKey =
  | 'totalPeopleBefore'
  | 'groupAdministrationBefore'
  | 'levelPositionBefore'
  | 'totalPeopleAfter'
  | 'groupAdministrationAfter'
  | 'levelPositionAfter';

interface EmployeeObservedFieldConfig {
  arrayName: 'before' | 'after';
  controlName: keyof EmployeeRowValue;
}

@Component({
  selector: 'app-project-correction',
  templateUrl: './project-correction.component.html',
  styleUrls: ['./project-correction.component.scss'],
  imports: [
    ConfirmDialog,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    AutoCompleteModule,
    ButtonsComponent,
    DesyAngularModule,
    PillComponent,
    DatePickerModule,
    SelectButtonModule,
    MultiSelectModule,
    ObservedFieldAlertComponent,
    ReviewCommentsBlockComponent,
  ],
  providers: [ConfirmationService],
})
export class ProjectCorrectionComponent implements OnInit {
  private projectsService = inject(ProjectsService);
  private route = inject(ActivatedRoute);
  id_project!: string;
  private projectCode!: number;
  private projectCodeRequest!: string;
  projectDetail = signal<ProjectDetails>({} as ProjectDetails);
  observedFields = signal<ReviewFieldKey[]>([]);
  pendingObservedFields = signal<string[]>([]);
  reviewComments: any[] = [];
  loading = signal<boolean>(true);
  observedFieldsCount = computed(() => this.pendingObservedFields().length);
  initialObservedFieldsCount = signal<number>(0);
  private initialObservedFields: string[] = [];
  private readonly employeeObservedFieldMap: Record<EmployeeObservedFieldKey, EmployeeObservedFieldConfig> = {
    totalPeopleBefore: { arrayName: 'before', controlName: 'totalPeople' },
    groupAdministrationBefore: { arrayName: 'before', controlName: 'groupAdministration' },
    levelPositionBefore: { arrayName: 'before', controlName: 'levelPosition' },
    totalPeopleAfter: { arrayName: 'after', controlName: 'totalPeople' },
    groupAdministrationAfter: { arrayName: 'after', controlName: 'groupAdministration' },
    levelPositionAfter: { arrayName: 'after', controlName: 'levelPosition' },
  };
  readonly canSubmitCorrection = computed(() => this.pendingObservedFields().length === 0);
  form: FormGroup;
  getError = getError;
  get currentDateFormatted(): string {
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date());
  }
  typeDataPersonalLabels: string[] = [];
  categoryDataPersonalLabels: string[] = [];
  typeDataPersonal = [
    { label: 'No trata datos personales', value: 2 },
    { label: 'Sí trata datos personales', value: 1 },
    { label: 'Datos anonimizados previamente', value: 3 },
    { label: 'Parcialmente seudonimizado', value: 4 },
    { label: 'Totalmente seudonimizados', value: 5 },
  ];
  categoryDataPersonal = [
    { label: 'No especial protección', value: 1 },
    { label: 'Datos de salud', value: 2 },
    { label: 'Origen étnico o racial', value: 3 },
    { label: 'Opiniones políticas, religiosas, sindicales', value: 4 },
    { label: 'Datos genéticos o biométricos', value: 5 },
    { label: 'Vida sexual u orientación', value: 6 },
  ];
  riskLevelOptions = [
    { label: 'Inaceptable', value: 'inaceptable' },
    { label: 'Alto', value: 'alto' },
    { label: 'Limitado', value: 'limitado' },
    { label: 'Mínimo', value: 'minimo' },
  ];
  typeDataPersonalOptions = this.typeDataPersonal.map((item) => item.label);
  categoryDataPersonalOptions = this.categoryDataPersonal.map((item) => item.label);
  organismsList = signal<{ organismo: string; codigo_organismo: string }[]>([]);

  usersList: string[] = [];
  proceduresList: string[] = [];
  procedureSearch: string = null;
  procedureSearchLoading = false;
  currentProcedures = signal<{ code: number; name: string }[]>([]);
  applications = signal<string[]>([]);
  application = signal<string>('');
  searchService = inject(SearchService);
  userLogged = JSON.parse(localStorage.getItem('userData') || '{}');
  fullName =
    this.userLogged.rpt +
    ' ' +
    '-' +
    ' ' +
    this.userLogged.givenName +
    ' ' +
    this.userLogged.surname1 +
    ' ' +
    this.userLogged.surname2 +
    ' ' +
    '-' +
    ' ' +
    this.userLogged.stationWork;
  formSubmitted = signal(false);

  private toNumber(value: unknown): number {
    return typeof value === 'number' ? value : Number(value);
  }

  private createEmployeeRow(values?: Partial<EmployeeRowValue>): FormGroup {
    return this.fb.group({
      totalPeople: [values?.totalPeople ?? null, [Validators.required, Validators.min(0)]],
      groupAdministration: [
        values?.groupAdministration ?? null,
        [Validators.required, Validators.maxLength(200), groupAdministrationFormatValidator()],
      ],
      levelPosition: [values?.levelPosition ?? null, [Validators.required, Validators.min(0), integerOnlyValidator()]],
    });
  }

  get before(): FormArray {
    return this.form.get('before') as FormArray;
  }

  get after(): FormArray {
    return this.form.get('after') as FormArray;
  }

  get beforeEmployeeRows(): FormGroup[] {
    return this.before.controls as FormGroup[];
  }

  get afterEmployeeRows(): FormGroup[] {
    return this.after.controls as FormGroup[];
  }

  constructor(
    private fb: FormBuilder,
    private location: Location,
    private confirmationService: ConfirmationService,
    private router: Router
  ) {
    this.form = this.fb.group({
      administrativeProcess: [null, [Validators.minLength(3), Validators.maxLength(500)]],
      averageResolutionEstimateTime: [null, Validators.required],
      averageResolutionRealTime: [null, Validators.required],
      biometricData: [''],
      categoryDataPersonalSelected: [[1]],
      contactEmailResponsible: [this.userLogged.email, [Validators.email, Validators.required]],
      economicEstimateAnnualMaintenance: [0],
      economicEstimateConsumption: [0],
      currentApplicationCostEstimate: [null, Validators.required],
      currentStaffCostEstimate: [null, Validators.required],
      postDeploymentStaffCostEstimate: [null, Validators.required],
      otherDerivedCosts: [null],
      economicEstimateImplementation: [0],
      eipd: [''],
      csvEipd: [null],
      isDataSensitive: ['no-realizada'],
      amountAffectedCitizens: [null, [Validators.required, Validators.min(0), Validators.pattern(/^[^:#]*$/)]],
      amountAffectedOfficials: [null, [Validators.required, Validators.min(0), Validators.pattern(/^[^:#]*$/)]],
      projectName: ['', [Validators.required, Validators.pattern(/^[^:#]*$/)]],
      RAT: ['', [Validators.required, Validators.pattern(/^[^:#]*$/)]],
      selectedProcedure: [[], [requiredNonEmptyArray]],
      systemFinality: ['', Validators.required],
      systemOrApp: [[], [requiredNonEmptyArray]],
      before: this.fb.array([this.createEmployeeRow()]),
      after: this.fb.array([this.createEmployeeRow()]),
      personalSelectedDataType: [0, Validators.required],
      projectClassification: ['', Validators.required],
      nif: [this.userLogged.nif],
      jobcode: [this.userLogged.roleCode],
      orgCode: [this.userLogged.orgCode],
      department: [{ value: this.userLogged.department, disabled: true }],
      selectedOrgCode: [{ value: this.userLogged.orgCode, disabled: true }],
      serviceName: [{ value: this.userLogged.serviceName, disabled: true }],
      generalDirectorateCode: [{ value: this.userLogged.generalDirectorateCode, disabled: true }],
      userNameResponsible: [{ value: this.fullName, disabled: true }],
    });

    effect(() => {
      const user = this.form.get('userNameResponsible')?.value;

      if (!user || typeof user !== 'object') {
        this.organismsList.set([
          {
            codigo_organismo: this.userLogged.orgCode,
            organismo: this.userLogged.orgName,
          },
        ]);
        return;
      }
      this.organismsList.set([
        {
          codigo_organismo: user.orgCode,
          organismo: user.organization,
        },
      ]);
    });
    effect(() => {
      const list = this.organismsList();
      if (!list.length) return;

      const formControl = this.form.get('selectedOrgCode');
      if (!formControl) return;

      const current = formControl.value;
      const match = list.find((o) => o.codigo_organismo === current);

      if (match) {
        formControl.setValue(current, { emitEvent: false });
      }
    });
    this.form.get('userNameResponsible')?.valueChanges.subscribe((user) => {
      const nifControl = this.form.get('nif');
      const jobCodeControl = this.form.get('jobcode');
      const orgCodeControl = this.form.get('selectedOrgCode');
      const departmentControl = this.form.get('department');
      const serviceNameControl = this.form.get('serviceName');

      if (!user || typeof user !== 'object') {
        nifControl.setValue(this.userLogged.nif);
        jobCodeControl.setValue(this.userLogged.roleCode);
        orgCodeControl.setValue(this.userLogged.orgCode);
        departmentControl.setValue(this.userLogged.department);
        serviceNameControl.setValue(this.userLogged.serviceName);
        return;
      }
      nifControl.setValue(user.nif);
      jobCodeControl.setValue(user.jobcode);
      orgCodeControl.setValue(user.orgCode);
      departmentControl.setValue(user.department);
      serviceNameControl.setValue(user.servicio);
    });

    this.form.get('personalSelectedDataType')?.valueChanges.subscribe((typeValue) => {
      const categoryControl = this.form.get('categoryDataPersonalSelected');
      const bioControl = this.form.get('biometricData');

      if (!categoryControl || !bioControl) return;

      if (typeValue === 1) {
        categoryControl.setValidators([requiredNonEmptyArray]);

        if (!Array.isArray(categoryControl.value)) {
          categoryControl.setValue([], { emitEvent: false });
        }
      } else {
        categoryControl.clearValidators();
        categoryControl.setValue([], { emitEvent: false });

        bioControl.clearValidators();
        bioControl.setValue('', { emitEvent: false });
        bioControl.updateValueAndValidity({ emitEvent: false });
      }

      categoryControl.updateValueAndValidity({ emitEvent: false });
    });

    this.form.get('categoryDataPersonalSelected')?.valueChanges.subscribe((categories) => {
      const bioControl = this.form.get('biometricData');

      if (Array.isArray(categories) && categories.includes(5)) {
        bioControl?.setValidators([Validators.required]);
      } else {
        bioControl?.clearValidators();
        bioControl?.setValue('');
      }

      bioControl?.updateValueAndValidity();
    });

    this.form.get('isDataSensitive')?.valueChanges.subscribe((value) => {
      this.toggleEffectiveDateValidators(value);
    });
    const initialUserName = this.form.get('userNameResponsible')?.value;
    if (!initialUserName || initialUserName.trim() === '') {
      this.form.get('selectedOrgCode')?.disable();
    }
  }
  async ngOnInit(): Promise<void> {
    this.id_project = this.route.snapshot.paramMap.get('id')!;
    this.loading.set(true);

    try {
      const { data } = await this.projectsService.getProjectById(this.id_project, 'CORRECTION', 'project');
      const detail = data as ProjectDetails;
      this.projectDetail.set(detail);
      this.reviewComments = [
        {
          userRole: 'GA',
          comment: detail.commentSupportGroup,
          state: detail.supportGroupState,
        },
        {
          userRole: 'RF',
          comment: detail.commentRevisorFuncional,
          state: detail.functionalReviewerState,
        },
        {
          userRole: 'RJ',
          comment: detail.commentRevisorJuridico,
          state: detail.legalReviewerState,
        },
        {
          userRole: 'RT',
          comment: detail.commentRevisorTecnico,
          state: detail.technicalReviewerState,
        },
        {
          userRole: 'RDPD',
          comment: detail.commentRevisorDpd,
          state: detail.dpdReviewerState,
        },
        {
          userRole: 'JS',
          comment: detail.commentServiceManager,
          state: detail.serviceManagerState,
        },
      ].filter((c) => c.comment);
      this.projectCode = detail.code;
      this.projectCodeRequest = detail.codeRequest;
      this.patchFormWithDetail(detail);
      this.processCorrectionFields(detail.correctionFields ?? []);
      this.applyObservedValidations();
      this.toggleEffectiveDateValidators(this.form.get('isDataSensitive')?.value);
      this.updateObservedFieldsFromFormStatus();
    } catch (error) {
      console.error('Error cargando detalle del proyecto:', error);
    } finally {
      this.loading.set(false);
    }
  }
  private patchFormWithDetail(detail: ProjectDetails): void {
    this.form.patchValue({
      projectName: detail.projectName ?? '',
      systemFinality: detail.systemFinality ?? '',
      selectedProcedure: detail.selectedProcedure ?? [],
      systemOrApp: detail.systemOrApp ?? [],
      otherDerivedCosts: detail.otherDerivedCosts ?? null,
      administrativeProcess: detail.administrativeProcess ?? null,
      amountAffectedCitizens: detail.amountAffectedCitizens ?? 0,
      amountAffectedOfficials: detail.amountAffectedOfficials ?? 0,
      currentApplicationCostEstimate: detail.currentApplicationCostEstimate ?? null,
      currentStaffCostEstimate: detail.currentStaffCostEstimate ?? null,
      postDeploymentStaffCostEstimate: detail.postDeploymentStaffCostEstimate ?? null,
      economicEstimateImplementation: detail.economicEstimateImplementation ?? 0,
      economicEstimateAnnualMaintenance: detail.economicEstimateAnnualMaintenance ?? 0,
      economicEstimateConsumption: detail.economicEstimateConsumption ?? 0,
      averageResolutionEstimateTime: detail.averageResolutionEstimateTime ?? null,
      averageResolutionRealTime: detail.averageResolutionRealTime ?? null,
      personalSelectedDataType: this.mapPersonalData(detail.personalSelectedDataType),
      categoryDataPersonalSelected: detail.categoryDataPersonalSelected ?? [],
      biometricData: detail.biometricData ?? '',
      RAT: detail.RAT ?? '',
      eipd: detail.eipd && detail.eipd !== 'No realizada' ? new Date(detail.eipd) : null,
      csvEipd: detail.csvEipd ?? null,
      isDataSensitive: detail.eipd && detail.eipd !== 'No realizada' ? 'seleccionar-fecha' : 'no-realizada',
      projectClassification: detail.projectClassification ?? '',
    });

    this.setEmployeeRows(
      'before',
      this.buildEmployeeRows(
        detail.totalPeopleBefore ?? [],
        detail.groupAdministrationBefore ?? [],
        detail.levelPositionBefore ?? []
      )
    );
    this.setEmployeeRows(
      'after',
      this.buildEmployeeRows(
        detail.totalPeopleAfter ?? [],
        detail.groupAdministrationAfter ?? [],
        detail.levelPositionAfter ?? []
      )
    );
    this.currentProcedures.set(detail.selectedProcedure ?? []);
    this.applications.set(detail.systemOrApp ?? []);
  }
  private buildEmployeeRows(
    totalPeople: number[],
    groupAdministration: string[],
    levelPosition: number[]
  ): FormGroup[] {
    const rowsLength = totalPeople.length || 1;
    const rows: FormGroup[] = [];

    for (let index = 0; index < rowsLength; index++) {
      rows.push(
        this.createEmployeeRow({
          totalPeople: totalPeople[index] ?? null,
          groupAdministration: groupAdministration[index] ?? null,
          levelPosition: levelPosition[index] ?? null,
        })
      );
    }

    return rows;
  }
  private setEmployeeRows(arrayName: 'before' | 'after', rows: FormGroup[]): void {
    this.form.setControl(arrayName, this.fb.array(rows));
    this.form.get(arrayName)?.markAsPristine();
  }
  private mapPersonalData(value: string | null | undefined): number {
    switch (value) {
      case 'no':
        return 2;
      case 'si':
        return 1;
      case 'parcial_seudonimizados':
        return 4;
      case 'total_seudonimizados':
        return 5;
      default:
        return 3;
    }
  }
  private getCorrectionRole(): string | null {
    const detail = this.projectDetail();
    const projectOrgCode = detail.orgCodeCreator;
    const projectOrgCandidates = projectOrgCode ? [projectOrgCode, `${environment.environment}${projectOrgCode}`] : [];
    const rolesForOrg = Object.entries(this.userLogged.roles ?? {})
      .filter(([, orgs]) => Array.isArray(orgs) && orgs.some((org) => projectOrgCandidates.includes(org)))
      .map(([roleKey]) => roleKey);

    if (rolesForOrg.includes('responsable_IA')) {
      return 'responsable_IA';
    }

    if (rolesForOrg.includes('jefe_consumidor')) {
      return 'jefe_consumidor';
    }

    return null;
  }
  private processCorrectionFields(anjanaFields: string[]): void {
    const formControls: ReviewFieldKey[] = anjanaFields
      .map((anjanaField) => anjanaFieldToReviewFieldMap[anjanaField])
      .filter((key): key is ReviewFieldKey => !!key);

    const uniqueFields = [...new Set(formControls)];

    this.initialObservedFields = uniqueFields;
    this.initialObservedFieldsCount.set(uniqueFields.length);
    this.observedFields.set(uniqueFields);
    this.pendingObservedFields.set(uniqueFields);

    console.log('Campos observados finales:', uniqueFields);
  }
  private applyObservedValidations(): void {
    this.observedFields().forEach((field) => {
      const employeeFieldConfig = this.getEmployeeObservedFieldConfig(field);
      if (employeeFieldConfig) {
        this.getEmployeeColumnControls(employeeFieldConfig).forEach((control) => control.updateValueAndValidity());
        return;
      }

      const control = this.form.get(field);
      if (!control) return;

      control.addValidators(Validators.required);
      control.updateValueAndValidity();
    });
  }
  private updateObservedFieldsFromFormStatus(): void {
    const employeeFieldsByArray: Record<'before' | 'after', EmployeeObservedFieldKey[]> = {
      before: [],
      after: [],
    };

    this.initialObservedFields.forEach((field) => {
      const employeeFieldConfig = this.getEmployeeObservedFieldConfig(field);
      if (employeeFieldConfig) {
        employeeFieldsByArray[employeeFieldConfig.arrayName].push(field as EmployeeObservedFieldKey);
        return;
      }

      const control = this.form.get(field);
      if (!control) return;

      control.valueChanges.subscribe(() => {
        const isPending = this.pendingObservedFields().includes(field);
        console.log(field, {
          value: control.value,
          valid: control.valid,
          dirty: control.dirty,
          errors: control.errors,
        });
        if (control.valid && isPending && (control.dirty || field === 'systemOrApp' || field === 'selectedProcedure')) {
          this.pendingObservedFields.update((fields) => fields.filter((f) => f !== field));
        }

        if (control.invalid && !isPending) {
          this.pendingObservedFields.update((fields) => [...fields, field]);
        }
      });
    });

    (Object.keys(employeeFieldsByArray) as ('before' | 'after')[]).forEach((arrayName) => {
      const fields = employeeFieldsByArray[arrayName];
      if (!fields.length) return;

      this.form.get(arrayName)?.valueChanges.subscribe(() => {
        fields.forEach((field) => this.updateEmployeeObservedFieldStatus(field));
      });
    });
  }

  private getEmployeeObservedFieldConfig(field: string): EmployeeObservedFieldConfig | null {
    return this.employeeObservedFieldMap[field as EmployeeObservedFieldKey] ?? null;
  }

  private getEmployeeColumnControls(config: EmployeeObservedFieldConfig): AbstractControl[] {
    return (this.form.get(config.arrayName) as FormArray).controls
      .map((row) => row.get(config.controlName))
      .filter((control): control is AbstractControl => !!control);
  }

  private updateEmployeeObservedFieldStatus(field: EmployeeObservedFieldKey, forceModified = false): void {
    const config = this.employeeObservedFieldMap[field];
    const controls = this.getEmployeeColumnControls(config);
    const isPending = this.pendingObservedFields().includes(field);
    const isColumnValid = controls.length > 0 && controls.every((control) => control.valid);
    const hasModification = controls.some((control) => control.dirty) || forceModified;

    if (isColumnValid && isPending && hasModification) {
      this.pendingObservedFields.update((fields) => fields.filter((f) => f !== field));
    }
  }

  isObserved(controlName: string): boolean {
    return this.pendingObservedFields().includes(controlName);
  }
  isAnyObserved(fields: string[]): boolean {
    return fields.some((field) => this.pendingObservedFields().includes(field));
  }

  hasInvalidObservedFields(): boolean {
    const hasInvalidObservedField = this.initialObservedFields.some((field) => this.isObservedFieldInvalid(field));
    const isCsvEipdApplicable = this.form.get('isDataSensitive')?.value === 'seleccionar-fecha';
    const isRequiredCsvEipdInvalid = isCsvEipdApplicable && (this.form.get('csvEipd')?.invalid ?? false);

    return hasInvalidObservedField || isRequiredCsvEipdInvalid;
  }

  private isObservedFieldInvalid(field: string): boolean {
    const employeeFieldConfig = this.getEmployeeObservedFieldConfig(field);
    if (employeeFieldConfig) {
      return this.getEmployeeColumnControls(employeeFieldConfig).some((control) => control.invalid);
    }

    return this.form.get(field)?.invalid ?? false;
  }

  onTypeDataPersonalChange(label: string) {
    const selected = this.typeDataPersonal.find((item) => item.label === label);
    this.form.get('personalSelectedDataType')?.setValue(selected?.value || null);
  }
  hasBiometricCategory(): boolean {
    const categories = this.form.get('categoryDataPersonalSelected')?.value;
    return Array.isArray(categories) && categories.includes(5);
  }
  getSelectedCategoriesText(values: any[]): string {
    if (!values) return '';
    return values.length > 2 ? `${values.length} opciones seleccionadas` : values.map((v) => v.label).join(', ');
  }

  getEmployeeRowError(row: FormGroup, controlName: string): string | null {
    return getError(row, controlName, this.formSubmitted());
  }

  addBeforeRow() {
    this.before.push(this.createEmployeeRow());
    this.before.markAsDirty();
    this.before.updateValueAndValidity();
    this.updateEmployeeObservedFieldsForArray('before');
  }

  removeBeforeRow(index: number) {
    if (this.before.length <= 1) return;

    this.before.removeAt(index);
    this.before.markAsDirty();
    this.before.updateValueAndValidity();
    this.updateEmployeeObservedFieldsForArray('before', true);
  }

  addAfterRow() {
    this.after.push(this.createEmployeeRow());
    this.after.markAsDirty();
    this.after.updateValueAndValidity();
    this.updateEmployeeObservedFieldsForArray('after');
  }

  removeAfterRow(index: number) {
    if (this.after.length <= 1) return;

    this.after.removeAt(index);
    this.after.markAsDirty();
    this.after.updateValueAndValidity();
    this.updateEmployeeObservedFieldsForArray('after', true);
  }

  private updateEmployeeObservedFieldsForArray(arrayName: 'before' | 'after', forceModified = false): void {
    this.initialObservedFields.forEach((field) => {
      const config = this.getEmployeeObservedFieldConfig(field);
      if (config?.arrayName === arrayName) {
        this.updateEmployeeObservedFieldStatus(field as EmployeeObservedFieldKey, forceModified);
      }
    });
  }

  private toggleEffectiveDateValidators(value: string) {
    const eff = this.form.get('eipd');
    const csvEipd = this.form.get('csvEipd');
    if (!eff || !csvEipd) return;

    eff.clearValidators();
    csvEipd.clearValidators();

    if (value === 'no-realizada') {
      eff.markAsDirty();
      csvEipd.markAsDirty();
      eff.setValue(null);
      csvEipd.setValue(null);
      eff.updateValueAndValidity({ emitEvent: true });
      csvEipd.updateValueAndValidity({ emitEvent: true });
      this.pendingObservedFields.update((fields) => fields.filter((field) => field !== 'csvEipd'));
      return;
    }

    if (this.isObserved('eipd')) {
      eff.addValidators(Validators.required);
    }
    const wasCsvEipdOriginallyObserved = this.initialObservedFields.includes('csvEipd');
    if (wasCsvEipdOriginallyObserved) {
      if (!this.pendingObservedFields().includes('csvEipd')) {
        this.pendingObservedFields.update((fields) => [...fields, 'csvEipd']);
      }
    }
    if (value === 'seleccionar-fecha') {
      csvEipd.addValidators(Validators.required);
      eff.addValidators((control) => {
        const val = control.value;
        if (!val) return null;

        const selectedDate = new Date(val);
        const today = new Date();

        selectedDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        return selectedDate <= today ? null : { dateInFuture: true };
      });
    }

    eff.updateValueAndValidity({ emitEvent: true });
    csvEipd.updateValueAndValidity({ emitEvent: true });
  }
  async onSearchOrganization(event) {
    const response: any = await this.searchService.search({ search: event.query, busqueda: 'organismos' });
    this.organismsList.set(response.data || []);
  }

  onOrganismSelected(event) {
    this.form.get('serviceName')?.enable();
    this.form.get('department')?.enable();
    this.form.patchValue({
      orgCode: event.value.codigo_organismo,
      department: event.value.departamento,
      serviceName: event.value.serviceName,
    });
    this.form.get('serviceName')?.disable();
    this.form.get('department')?.disable();
  }

  async onSearchUser(event) {
    const response: any = await this.searchService.search({ search: event.query, busqueda: 'personas' });
    this.usersList = response.data.map((user) => ({
      infoCompleted: `${user.rpt} - ${user.name} - ${user.workstation}`,
      nif: user.nif,
      orgCode: user.orgcode,
      jobcode: user.jobcode,
      department: user.department,
      organization: user.organization,
      serviceName: user.servicio,
      email: user.email || 'Introduzca un email válido',
      generalDirectorateCode: user.codigo_direccion_general,
    }));
    this.form.patchValue({
      contactEmailResponsible: event.value?.email,
    });
  }

  onUserSelected(event: any) {
    const user = event.value;
    this.form.patchValue({
      userNameResponsible: user.infoCompleted,
      nif: user.nif,
      jobcode: user.jobcode,
      department: user.department,
      selectedOrgCode: user.orgCode,
      serviceName: user.serviceName,
      contactEmailResponsible: user.email,
      generalDirectorateCode: user.generalDirectorateCode,
    });
    this.organismsList.set([
      {
        codigo_organismo: user.orgCode,
        organismo: user.organization,
      },
    ]);

    this.organismsList.set([...this.organismsList()]);
  }

  async onSearchProcedure(event: any) {
    this.procedureSearchLoading = true;
    this.proceduresList = [];
    try {
      const response: any = await this.searchService.search({ search: event.query, busqueda: 'procedimientos' });
      this.proceduresList = response.data || [];
    } finally {
      this.procedureSearchLoading = false;
    }
  }

  searchCurrentProcedure(autocomplete: AutoComplete, event?: Event) {
    const query = (this.procedureSearch ?? '').trim();
    if (query.length < 3) return;

    autocomplete.search(event, query, 'input');
  }

  onProcedureSearchEnter(event: Event, autocomplete: AutoComplete) {
    const activeOption = (event.target as HTMLElement | null)?.getAttribute('aria-activedescendant');
    if (activeOption) return;

    event.preventDefault();
    this.searchCurrentProcedure(autocomplete, event);
  }

  onProcedureSelected(event: any) {
    this.currentProcedures.update((arr) => [...arr, event.value]);
    const control = this.form.get('selectedProcedure');
    control?.setValue(this.currentProcedures());
    control?.markAsDirty();
    setTimeout(() => this.clearProcedureSearch());
  }

  clearProcedureSearch() {
    this.procedureSearch = null;
    this.proceduresList = [];
    this.procedureSearchLoading = false;
  }
  removeProcedure(item: number) {
    const current = this.form.get('selectedProcedure')?.value || [];
    const updated = current.filter((i: any) => i.code !== item);
    const control = this.form.get('selectedProcedure');
    control?.setValue(updated);
    control?.markAsDirty();
    this.currentProcedures.update((arr: any[]) => arr.filter((i: any) => i.code !== item));
  }

  addApplication(event?: MouseEvent) {
    event.preventDefault();
    const value = this.application();
    if (value) {
      this.applications.update((arr) => [...arr, value]);
      this.form.get('systemOrApp')?.setValue(this.applications());
      this.form.get('systemOrApp')?.markAsDirty();
      this.application.set('');
    }
  }

  removeApplication(item: string) {
    this.applications.update((apps) => apps.filter((a) => a !== item));
    this.form.get('systemOrApp')?.setValue(this.applications());
    this.form.get('systemOrApp')?.markAsDirty();
  }

  async onSave() {
    console.log('FORM INVALID?', this.form.invalid);
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (control?.invalid) {
        console.log('CONTROL INVALID:', key, control.errors);
      }
    });

    this.formSubmitted.set(true);

    if (!this.canSubmitCorrection() || this.hasInvalidObservedFields()) {
      this.form.markAllAsTouched();
      return;
    }
    try {
      const { RAT: rat, before, after, ...raw } = this.form.getRawValue();
      const beforeRows = before as EmployeeRowValue[];
      const afterRows = after as EmployeeRowValue[];
      const role = this.getCorrectionRole();
      console.log('rol subsanando:', role);
      console.log('rol enviado', {
        ...raw,
        rat,
        role,
      });
      console.log('Observado:', this.observedFields());
      const payload: ProjectCorrectionPayload = {
        ...raw,
        rat,
        administrativeProcess: raw.administrativeProcess?.trim?.() || null,
        totalPeopleBefore: beforeRows.map((row) => this.toNumber(row.totalPeople)),
        groupAdministrationBefore: beforeRows.map((row) => row.groupAdministration ?? ''),
        levelPositionBefore: beforeRows.map((row) => this.toNumber(row.levelPosition)),
        totalPeopleAfter: afterRows.map((row) => this.toNumber(row.totalPeople)),
        groupAdministrationAfter: afterRows.map((row) => row.groupAdministration ?? ''),
        levelPositionAfter: afterRows.map((row) => this.toNumber(row.levelPosition)),
        proyectId: this.projectCode,
        proyectIdRequest: this.projectCodeRequest,
        role,
      };
      setTimeout(() => {
        console.log('Observado después de enviar:', this.observedFields());
      });
      await this.projectsService.submitProjectCorrection(payload);
      this.router.navigate(['/catalogo-ia/proyectos/listado-proyectos']);
    } catch (error) {
      console.error('Error al enviar subsanación:', error);
    }
  }

  backPage(): void {
    this.location.back();
  }
  confirm() {
    this.confirmationService.confirm({
      message: '¿Seguro que desea enviar la subsanación?',
      header: 'Confirmación',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancelar',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Enviar subsanación',
      },
      accept: () => {
        this.onSave();
      },
    });
  }
}
